import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { TranscodeProgress } from './useTranscodeStatus';
import { StepError, StepMeta, StepNode, StepNodeTop, StepPill, StepProgress, StepState, whereOf } from '../Runs/RunStatusUi';

const BACKENDS: Record<string, string> = {
  Orchestrator: 'NVENC',
  Gcp: 'Google Transcoder',
};

export const displayState = (item: TranscodeProgress): StepState => {
  switch (item.state) {
    case 'Succeeded':
      return 'done';
    case 'Failed':
      return 'failed';
    case 'Copying':
      return 'running';
    case 'Running':
      return item.jobState === 'Pending' || item.jobState === 'None' || !item.jobId ? 'queued' : 'running';
    default:
      return 'waiting';
  }
};

export const cameraLabel = (index: number, count: number) => {
  if (count === 2) return index === 0 ? 'left camera' : 'right camera';
  return `camera ${index + 1}`;
};

export const backendLabel = (backend?: string | null) => (backend ? BACKENDS[backend] || backend : '');

export const useTick = (active: boolean) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
`;

interface Props {
  items: TranscodeProgress[];
  showSession?: boolean;
}

const TranscodeStatusList: React.FunctionComponent<Props> = ({ items, showSession }) => (
  <Grid>
    {items.map((item, index) => {
      const state = displayState(item);
      const percent = state === 'done' ? 100 : item.percent;
      const where = whereOf(item.pool) || backendLabel(item.backend);
      return (
        <StepNode key={item.videoId} state={state}>
          <StepNodeTop>
            <span>
              {showSession ? (
                <Link to={`/session/transcode/${item.sessionId}`}>
                  Transcode · session {item.sessionId}
                  {item.sessionLabel ? ` · ${item.sessionLabel}` : ''}
                </Link>
              ) : (
                `Transcode · ${cameraLabel(index, items.length)}`
              )}
            </span>
            <StepPill state={state} />
          </StepNodeTop>
          <StepMeta>
            {[showSession ? `Video ${item.videoId}` : '', where, item.state === 'Copying' ? item.step : '']
              .filter(Boolean)
              .join(' · ')}
          </StepMeta>
          {percent != null && (state === 'running' || state === 'done') && <StepProgress percent={percent} state={state} />}
          {state === 'failed' && item.error && <StepError>{item.error}</StepError>}
        </StepNode>
      );
    })}
  </Grid>
);

export default TranscodeStatusList;
